import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import OutboxOutlinedIcon from "@mui/icons-material/OutboxOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { formatBudget, getStepPath } from "@/lib/campaigns";
import {
  getActivationSummary,
  getDeliveryMethodLabel,
  getExpirationSummary,
  getScheduleSummary,
  getSendingToLabel,
} from "@/lib/reviewSummary";
import { isSelfSendMode } from "@/utils/campaignMode-helper";
import { resolveCampaignTimeZone } from "@/utils/timezone-helper";
import ReviewActionButton from "../ReviewActionButton/ReviewActionButton";
import ReviewCard from "../ReviewCard/ReviewCard";
import SettingsReviewRow from "../SettingsReviewRow/SettingsReviewRow";

/**
 * "Settings Review" card — a running list of campaign settings. Mirrors the
 * prototype's renderReviewSummary() (Campaign-Builder-Master-Prototype.html,
 * ~line 22767-22845) and markup (~line 10596-10613). The live JS is the
 * source of truth: a single header Edit button (no per-row edit buttons),
 * and the Scheduled row is dropped entirely in manual-send mode.
 *
 * Every icon here is the outlined variant: the prototype renders each one
 * through `<span class="material-icons-outlined">`, both in the row template
 * and in the header's Edit button.
 */
export default function SettingsReviewCard() {
  const navigate = useNavigate();
  const {
    selectedBudget,
    recipients,
    notificationMode,
    scheduleMode,
    scheduleDate,
    scheduleTime,
    scheduleTz,
    expiryMode,
    customExpiryDate,
    activationMode,
    activationDate,
    activationTime,
    activationTz,
  } = useCampaignBuilder();

  // Self-send (manual + universal): no scheduled email send — the row shows link ACTIVATION instead,
  // and the delivery icon is the self-distribute outbox rather than an envelope.
  const isSelfSend = isSelfSendMode(notificationMode);

  const collectionValue = useMemo(
    () => (selectedBudget !== null ? formatBudget(selectedBudget) : "—"),
    [selectedBudget],
  );
  // Step 2's recipient list is the destination source, not Step 1's base-collection country.
  const sendingToValue = useMemo(() => getSendingToLabel(recipients), [recipients]);
  const deliveryValue = useMemo(() => getDeliveryMethodLabel(notificationMode), [notificationMode]);
  const scheduleValue = useMemo(
    () => getScheduleSummary({ scheduleMode, scheduleDate, scheduleTime, scheduleTz }),
    [scheduleMode, scheduleDate, scheduleTime, scheduleTz],
  );
  const expirationValue = useMemo(
    () =>
      getExpirationSummary({
        expiryMode,
        customExpiryDate,
        notificationMode,
        timeZone: resolveCampaignTimeZone({ notificationMode, scheduleTz, activationTz }),
      }),
    [expiryMode, customExpiryDate, notificationMode, scheduleTz, activationTz],
  );
  const activationValue = useMemo(
    () =>
      getActivationSummary({
        activationMode,
        activationDate,
        activationTime,
        timeZone: resolveCampaignTimeZone({ notificationMode, scheduleTz, activationTz }),
      }),
    [activationMode, activationDate, activationTime, notificationMode, scheduleTz, activationTz],
  );

  const handleEdit = useCallback(() => {
    navigate(getStepPath(1));
  }, [navigate]);

  return (
    <ReviewCard
      title="Settings Review"
      actions={
        <ReviewActionButton
          label="Edit"
          icon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={handleEdit}
        />
      }
    >
      <div className="flex flex-col border-t border-card-border">
        <SettingsReviewRow
          icon={<ShoppingBagOutlinedIcon sx={{ fontSize: 20 }} />}
          label="Collection"
          value={collectionValue}
        />
        <SettingsReviewRow
          icon={<PublicOutlinedIcon sx={{ fontSize: 20 }} />}
          label="Sending To"
          value={sendingToValue}
        />
        <SettingsReviewRow
          icon={
            isSelfSend ? (
              <OutboxOutlinedIcon sx={{ fontSize: 20 }} />
            ) : (
              <MailOutlineIcon sx={{ fontSize: 20 }} />
            )
          }
          label="Delivery Method"
          value={deliveryValue}
        />
        {isSelfSend ? (
          <SettingsReviewRow
            icon={<LinkOutlinedIcon sx={{ fontSize: 20 }} />}
            label="Links Activate"
            value={activationValue}
          />
        ) : (
          <SettingsReviewRow
            icon={<ScheduleOutlinedIcon sx={{ fontSize: 20 }} />}
            label="Scheduled"
            value={scheduleValue}
          />
        )}
        <SettingsReviewRow
          icon={<EventBusyOutlinedIcon sx={{ fontSize: 20 }} />}
          label="Expiration"
          value={expirationValue}
        />
      </div>
    </ReviewCard>
  );
}
